// Wizard (TEMPLATES.md): one question per step, a centered card, dots for
// progress, Back and Next pinned at the bottom, Enter is Next. Full screen
// on every tier; the frame is still the viewport and the card scrolls.

import { useEffect, type ReactNode } from 'react';
import { Button, Caption, Text } from '../kit/ui';

export interface WizardStep {
  id: string;
  title: string;
  /** One sentence under the title, in the voice guide. */
  hint?: string;
  content: ReactNode;
  /** A sentence that previews what the answer means; secondary ink under the control. */
  preview?: string;
  /** Inline problem, blocks Next. */
  error?: string | null;
  /** Label for the last step's button. */
  doneLabel?: string;
  /**
   * A wider card on a tablet and up, for a step that is a table with a rail
   * beside it rather than one control: the scoring step (PLAN-2026-10-18 §2.8).
   */
  wide?: boolean;
}

export function Wizard({
  title,
  steps,
  index,
  onIndex,
  onDone,
  onCancel,
}: {
  title: string;
  steps: WizardStep[];
  index: number;
  onIndex: (i: number) => void;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const step = steps[index]!;
  const last = index === steps.length - 1;
  const blocked = Boolean(step.error);

  const next = () => {
    if (blocked) return;
    if (last) onDone();
    else onIndex(index + 1);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'TEXTAREA' || t.tagName === 'BUTTON')) return;
      e.preventDefault();
      next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="fsp-screen fsp-wizard">
      <div className="fsp-wizard-scroll">
        <div className={`fsp-wizard-card fsp-surface${step.wide ? ' fsp-wizard-card-wide' : ''}`}>
          <Caption>{title}</Caption>
          {/* One step has no progress to show, and "Step 1 of 1" says nothing. */}
          {steps.length > 1 ? (
            <div
              className="fsp-wizard-dots"
              role="img"
              aria-label={`Step ${index + 1} of ${steps.length}`}
            >
              {steps.map((s, i) => (
                <span
                  key={s.id}
                  className={`fsp-wizard-dot ${i === index ? 'active' : i < index ? 'done' : ''}`}
                />
              ))}
            </div>
          ) : null}
          <h2 className="fsp-disp fsp-wizard-title">{step.title}</h2>
          {step.hint ? (
            <Text tone="secondary" block>
              {step.hint}
            </Text>
          ) : null}
          <div className="fsp-wizard-body">{step.content}</div>
          {step.preview ? <div className="fsp-wizard-preview">{step.preview}</div> : null}
          {step.error ? (
            <Text tone="warn" block>
              {step.error}
            </Text>
          ) : null}
        </div>
      </div>
      <div className="fsp-wizard-bar">
        {index === 0 ? (
          onCancel ? (
            <Button variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
          ) : (
            <span />
          )
        ) : (
          <Button variant="quiet" icon="back" onClick={() => onIndex(index - 1)}>
            Back
          </Button>
        )}
        <Button
          variant="primary"
          size="md"
          iconRight={last ? undefined : 'forward'}
          onClick={next}
          disabled={blocked}
        >
          {last ? (step.doneLabel ?? 'Done') : 'Next'}
        </Button>
      </div>
    </div>
  );
}
